import { memo, useCallback, useMemo, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import {
  reservationTemplatesSelector,
  selectedReservationTemplateMap,
} from 'slices/reservationTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  checkFirstTimeRuleExists,
  createEmptyTimeRule,
  deleteTimeRule,
  duplicateTimeRule,
  selectedTimeRuleIdsSelector,
  setFirstTimeRule,
  setSecondTimeRule,
  timeRuleLoadingSelector,
  timeRuleMapSelector,
  timeRulesSelector,
} from 'slices/timeRule.slice';
import { matchString } from 'utils/stringUtils';
import type { TTimeRule } from '@timeedit/types';
import { hasPermissions } from '@timeedit/types/lib/utils/permissionCalculator';
import {
  Button,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
} from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
// Components
// Types

// Slices
// Utils
// Styles
import './TimeRuleList.scss';

interface IRowData {
  id: number;
  name: string;
  type: string;
  templates: string;
}

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const TimeRuleList = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();

  const timeRules: TTimeRule[] = useAppSelector(timeRulesSelector);
  const timeRuleMap: Record<number, TTimeRule> =
    useAppSelector(timeRuleMapSelector);
  const selectedIds: number[] = useAppSelector(selectedTimeRuleIdsSelector);
  const templateMap = useAppSelector(selectedReservationTemplateMap);
  const reservationTemplates = useAppSelector(reservationTemplatesSelector);
  const user = useAppSelector((state) => state.auth.user);

  const userIsSuper = useMemo(
    () =>
      user &&
      hasPermissions(user?.appPermissions, user?.scopes, {
        scopes: ['TE_ADMIN::super'],
      }),
    [user]
  );

  const filterOptions = useMemo(() => {
    return {
      templates: reservationTemplates.map((template) => ({
        value: template.name,
        label: template.name,
      })),
      type: ['Standard', 'Time Slot'].map((value) => ({ value, label: value })),
    };
  }, [reservationTemplates]);

  const loading = useAppSelector(timeRuleLoadingSelector);

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const filterData: IRowData[] = useMemo(() => {
    const dataSource = timeRules.map((item) => ({
      id: item.id,
      name: item.name || getText('new_time_rule'),
      type: item.timeSlotSelection ? 'Time Slot' : 'Standard',
      templates: item.templates
        ?.map((templateId) => templateMap[templateId]?.name)
        .filter((name) => name && name !== '')
        .join(', '),
    }));

    return dataSource.filter((pr) => {
      let matchedText = true;
      let matchedType = true;
      let matchedTemplate = true;
      const {
        freeTextSearch,
        filters: { type, templates },
      } = filterSettings;
      if (freeTextSearch) {
        matchedText = matchString(pr.name, freeTextSearch);
      }
      if (type) {
        matchedType = type === pr.type;
      }
      if (templates) {
        matchedTemplate = matchString(pr.templates, templates as string);
      }
      return matchedText && matchedType && matchedTemplate;
    });
  }, [timeRules, filterSettings]);

  const onCreate = useCallback(() => {
    confirmDiscardPaddingChanges(true, () => dispatch(createEmptyTimeRule()));
  }, []);

  const onDuplicate = useCallback(
    (record: IRowData) => {
      confirmDiscardPaddingChanges(true, () =>
        dispatch(duplicateTimeRule(timeRuleMap[record.id]))
      );
    },
    [timeRuleMap]
  );

  const onCompare = useCallback(
    (record: IRowData) => {
      const firstTimeRuleExist = dispatch(checkFirstTimeRuleExists());
      if (!firstTimeRuleExist) {
        return dispatch(setFirstTimeRule(timeRuleMap[record.id]));
      }
      confirmDiscardPaddingChanges(false, () =>
        dispatch(setSecondTimeRule(timeRuleMap[record.id]))
      );
    },
    [timeRuleMap]
  );

  const onRowClick = useCallback(
    (record: IRowData) => {
      confirmDiscardPaddingChanges(true, () =>
        dispatch(setFirstTimeRule(timeRuleMap[record.id]))
      );
    },
    [timeRuleMap]
  );

  const onRow = useRowProps(onRowClick, selectedIds);

  const onDelete = useCallback(
    (record: IRowData) =>
      confirmDelete(record.name, () => dispatch(deleteTimeRule(record.id))),
    [confirmDelete, dispatch]
  );
  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(checkFirstTimeRuleExists())),
    [dispatch]
  );
  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps<IRowData>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  const footer = useCallback(
    () => (
      <Space>
        <Button
          size="small"
          type="primary"
          onClick={onCreate}
          disabled={!userIsSuper}
        >
          Create new
        </Button>
      </Space>
    ),
    [onCreate, userIsSuper]
  );

  const title = useTableTitle<IRowData>({
    total: timeRules.length,
    filteredAmount: filterData.length,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <div className="time-rule-list" ref={contentRef}>
      <Filter
        defaultFields={defaultFields}
        options={filterOptions}
        settings={filterSettings}
        onChange={setFilterSettings}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={filterData}
          columns={columns}
          getMenuProps={menuProps}
          onRow={onRow}
          footer={footer}
          title={title}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default memo(TimeRuleList);

const columns = [
  {
    key: 'name',
    dataIndex: 'name',
    title: 'Name',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.name?.localeCompare(item2.name),
  },
  {
    key: 'type',
    dataIndex: 'type',
    title: 'Type',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.type?.localeCompare(item2.type),
  },
  {
    key: 'templates',
    dataIndex: 'templates',
    title: 'Reservation Templates',
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.templates?.localeCompare(item2.templates),
  },
];

const defaultFields: TEIFilterField[] = [
  {
    key: 'type',
    label: 'Type',
    type: TEEFilterType.text,
  },
  {
    key: 'templates',
    label: 'Reservation Templates',
    type: TEEFilterType.text,
  },
];
